import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { motion } from "motion/react";
import { CalendarDays, Clock, UserRound, ChevronRight } from "lucide-react";
import client, { urlFor } from "../sanityClient";
import { PortableText } from "@portabletext/react";
import { portableTextComponents } from "../components/PortableTextComponents";
import { Helmet } from "react-helmet";

function BlogDetails() {
  const { t, i18n } = useTranslation();
  const { slug } = useParams();
  const lang = i18n.language;
  const [post, setPost] = useState<any>(null);

  useEffect(() => {
    const POSTS_QUERY = `*[
      _type == "post" && slug.current == "${slug}"
    ][0]{_id, title, slug, image, body, description, author, publishedAt, readingTime, tags[]->}`;
    client.fetch(POSTS_QUERY).then(setPost).catch(console.error);
  }, [slug]);

  const postImageUrl = post?.image ? urlFor(post.image)?.width(1200).height(675).url() : null;
  const title = post?.title?.[lang] || "";

  return (
    <>
      <Helmet>
        <title>{title || "Blog"} — EliteSoft</title>
        <meta name="description" content={post?.description?.[lang] || ""} />
        <meta property="og:locale" content={lang} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={post?.description?.[lang] || ""} />
        <meta property="og:url" content={post?.slug?.current || ""} />
        <meta property="og:site_name" content="elitesoft.iq" />
        <meta property="article:published_time" content={post?.publishedAt || ""} />
        {postImageUrl && <meta property="og:image" content={postImageUrl} />}
        <meta property="og:image:type" content="image/jpeg" />
        <meta name="author" content={post?.author || ""} />
      </Helmet>

      <article className="bg-white">
        {/* Header */}
        <header className="bg-canvas border-b border-gray-100 px-6 sm:px-8 pt-12 pb-14">
          <div className="max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-sm text-brand/50 mb-6 flex-wrap">
              <Link to="/" className="hover:text-brand transition-colors">{t("common.home")}</Link>
              <ChevronRight className="w-4 h-4 rtl:rotate-180" />
              <Link to="/blog" className="hover:text-brand transition-colors">{t("common.blog")}</Link>
              {title && (
                <>
                  <ChevronRight className="w-4 h-4 rtl:rotate-180" />
                  <span className="text-brand/70 truncate max-w-[200px]">{title}</span>
                </>
              )}
            </nav>

            {post?.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-4">
                {post.tags.map((tag: any, i: number) => (
                  <span key={i} className="px-3 py-1 rounded-full bg-lime/20 text-brand text-xs font-semibold">
                    {tag.label?.[lang]}
                  </span>
                ))}
              </div>
            )}

            <h1 className="text-3xl md:text-4xl font-extrabold leading-tight mb-5">{title}</h1>

            <div className="flex flex-wrap items-center gap-5 text-sm text-brand/55">
              {post?.publishedAt && (
                <span className="flex items-center gap-1.5">
                  <CalendarDays className="w-4 h-4" /> {new Date(post.publishedAt).toLocaleDateString()}
                </span>
              )}
              {post?.readingTime && (
                <span className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" /> {post.readingTime} {t("blog.minutes")}
                </span>
              )}
              {post?.author && (
                <span className="flex items-center gap-1.5">
                  <UserRound className="w-4 h-4" /> {t("blog.author")}: {post.author}
                </span>
              )}
            </div>
          </div>
        </header>

        <div className="max-w-3xl mx-auto px-6 sm:px-8 py-12">
          {postImageUrl && (
            <motion.img
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              src={postImageUrl}
              alt={title}
              className="w-full rounded-3xl mb-10 object-cover"
            />
          )}

          <div className="prose-content text-brand/80 leading-relaxed">
            {post?.description?.[lang] && (
              <PortableText value={post.description[lang]} components={portableTextComponents} />
            )}
            {post?.body?.[lang] && (
              <PortableText value={post.body[lang]} components={portableTextComponents} />
            )}
          </div>

          <div className="mt-12 pt-8 border-t border-gray-100">
            <Link to="/blog" className="inline-flex items-center gap-2 text-brand font-bold text-sm hover:text-brand-teal transition-colors">
              <ChevronRight className="w-4 h-4 rotate-180 rtl:rotate-0" /> {t("common.blog")}
            </Link>
          </div>
        </div>
      </article>
    </>
  );
}

export default BlogDetails;
